import { useMemo } from 'react';
import { Button, TableColumnsType, TableProps, Tooltip } from 'antd';
import { useTranslate } from '@tolgee/react';
import ScrollTable from 'components/ScrollTable';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';
import { DIMENSION_META, METRIC_META, NIL_TOOLTIP } from './constants';
import { formatMetricValue, formatPercent } from './utils';
import SpsStatusPill from './SpsStatusPill';

type RT = Dashboard.TikTokUS.ShopOverviewRow;
type MetricCode = Dashboard.TikTokUS.MetricCode;
type ProblemOrderMetricCode = Dashboard.TikTokUS.ProblemOrderMetricCode;

export interface SpsOverviewTableProps extends TableProps<RT> {
  /** 当前选中的维度 —— 决定动态展开哪 2 个指标列 */
  dimension: Dashboard.TikTokUS.DimensionName;
  onCheckTrend: (record: RT) => void;
  onCheckProblemOrder: (record: RT, metricCode: ProblemOrderMetricCode) => void;
  onCheckAftersales: (record: RT) => void;
  onCheckDsat: (record: RT) => void;
}

/** 数据不足 / 无值：统一显示 `-` + tooltip，**不按 0 分处理** */
function Nil() {
  const { t } = useTranslate();
  return (
    <Tooltip title={t(NIL_TOOLTIP)}>
      <span style={{ color: 'var(--fett-text-tertiary)' }}>-</span>
    </Tooltip>
  );
}

function SpsOverviewTable(props: SpsOverviewTableProps) {
  const {
    dimension,
    dataSource,
    onCheckTrend,
    onCheckProblemOrder,
    onCheckAftersales,
    onCheckDsat,
    ...otherProps
  } = props;

  const { t } = useTranslate();
  const { config } = useStoreNameConfig();

  /** 当前维度下要展开的 2 个指标（顺序固定） */
  const metricCodes = useMemo<MetricCode[]>(
    () => DIMENSION_META.find(({ value }) => value === dimension)?.metrics ?? [],
    [dimension],
  );

  /** 指标列旁的【明细】入口 —— 跟着指标列走，不堆在操作列 */
  const detailEntry = (record: RT, code: MetricCode) => {
    const label = t('todo.明细');

    if (code === 'IM_DSAT') {
      return (
        <Button type="link" onClick={() => onCheckDsat(record)}>
          {label}
        </Button>
      );
    }
    if (code === 'AHT') {
      return (
        <Button type="link" onClick={() => onCheckAftersales(record)}>
          {label}
        </Button>
      );
    }
    if (METRIC_META[code].supportsProblemOrders) {
      return (
        <Button
          type="link"
          onClick={() => onCheckProblemOrder(record, code as ProblemOrderMetricCode)}
        >
          {label}
        </Button>
      );
    }
    return null;
  };

  const columns: TableColumnsType<RT> = [
    {
      key: 'storeName',
      dataIndex: 'storeName',
      title: t('店铺名称'),
      width: 200,
      // 与其它平台（Shopee/Lazada/TikTok）完全一致：按 isStoreAliasFirst **只展示一个**名称，
      // 不要同时展示名称和别名
      render: (_, { storeName, storeAlias, region }) =>
        isStoreAliasFirst(config, 'TIKTOK_US', region) ? storeAlias || storeName : storeName,
    },
    {
      key: 'spsScore',
      dataIndex: 'spsScore',
      title: t('SPS总分'),
      width: 110,
      /** 三态：不排序 → 降序 → 升序；排序由服务端全局完成，前端不本地排序 */
      sorter: true,
      /** 第一下点给降序（与「默认按订单量降序」的直觉一致），再点升序，第三下回到不排 */
      sortDirections: ['descend', 'ascend'],
      showSorterTooltip: false,
      render: (_, { spsScore }) => (spsScore ? <span>{spsScore}</span> : <Nil />),
    },
    {
      key: 'spsTier',
      dataIndex: 'spsTier',
      title: t('分档'),
      width: 110,
      render: (_, { spsTier }) =>
        !spsTier || spsTier === 'NIL' ? (
          <Nil />
        ) : (
          <SpsStatusPill status={spsTier}>{spsTier}</SpsStatusPill>
        ),
    },
    {
      key: 'peerPercentile',
      dataIndex: 'peerPercentile',
      title: t('同类百分位'),
      width: 120,
      render: (_, { peerPercentile }) =>
        peerPercentile ? <span>{formatPercent(peerPercentile)}</span> : <Nil />,
    },
    ...metricCodes.map<TableColumnsType<RT>[number]>((code) => ({
      key: code,
      // 表头不放 ⓘ（需求方明确要求去掉）；口径说明在页内「数据说明」气泡里
      title: t(METRIC_META[code].nameZh),
      width: 210,
      render: (_, record: RT) => {
        const item = record.metrics?.find(({ metricCode }) => metricCode === code);
        const entry = detailEntry(record, code);
        if (!item || item.status === 'NIL') {
          return (
            <div className="flex items-center">
              <Nil />
              {entry}
            </div>
          );
        }
        return (
          <div className="flex items-center">
            <span>{formatMetricValue(item.value, item.valueUnit)}</span>
            <SpsStatusPill className="ml-1" status={item.status}>
              {item.status}
            </SpsStatusPill>
            {entry}
          </div>
        );
      },
    })),
    {
      key: 'operate',
      title: t('操作'),
      width: 116,
      fixed: 'right',
      render: (_, record) => (
        <Button type="link" onClick={() => onCheckTrend(record)}>
          {t('查看趋势')}
        </Button>
      ),
    },
  ];

  return <ScrollTable dataSource={dataSource} columns={columns} {...otherProps} />;
}

export default SpsOverviewTable;
